import React from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import './styles/toastify.css';
import { PersistGate } from 'redux-persist/integration/react';
import { Provider } from 'react-redux';
import { persistor, store } from 'redux/store';
import { authManager } from 'services/auth';
import { authStatusChanged, setRealmRoles } from 'redux/reducers/auth/reducer';
import { ToastContainer } from 'react-toastify';
import { ThemeProvider } from '@emotion/react';
import { createTheme, darken, lighten } from '@mui/material/styles';
import { alertClasses, AlertColor } from '@mui/material/Alert';
import { getTheme, COLORS } from 'services/constants/colors';
import CssBaseline from '@mui/material/CssBaseline';
import App from './App';
import reportWebVitals from './reportWebVitals';
import 'react-toastify/dist/ReactToastify.css';
import 'flag-icons/css/flag-icons.min.css';
// import './assets/styles/index.scss';

const rootElement = document.getElementById('root') as HTMLElement;

const root = createRoot(rootElement); // createRoot(container!) if you use TypeScript

authManager.subscribe((status) => store.dispatch(authStatusChanged(status)));
authManager.subscribeRealmRoles((roles) => store.dispatch(setRealmRoles(roles)));

void authManager.bootstrap();

const themeName = getTheme();
// Ensure theme class is set on <html> for CSS-based theming (e.g., scrollbar styling)
const rootHtml = document.documentElement;
rootHtml.classList.remove('theme-dark', 'theme-light', 'theme-light-gray');
const themeClass =
	themeName === 'dark'
		? 'theme-dark'
		: themeName === 'light_gray'
			? 'theme-light-gray'
			: 'theme-light';
rootHtml.classList.add(themeClass);
const mode = themeName === 'dark' ? 'dark' : 'light';
const theme = createTheme({
	palette: {
		mode,
		primary: {
			main: COLORS.PRIMARY,
			contrastText: '#fff',
			light: COLORS.SECONDARY,
		},
		secondary: {
			main: '#7ea13a',
			contrastText: '#fff',
		},
		success: {
			main: '#7ea13a',
			contrastText: '#fff',
			light: '#7ea13a80',
		},
		error: {
			main: '#d32f2f',
			contrastText: '#fff',
			light: '#d32f2f7d',
		},
		info: {
			main: '#6ec1e4',
			light: '#ace1f780',
		},
		warning: {
			main: '#e7af00',
			light: '#e7af0080',
		},
		background: {
			default: COLORS.BACKGROUND,
			paper: COLORS.PAPER,
		},
		text: {
			primary: COLORS.font_main,
			secondary: COLORS.font_status,
		},
		grey: {
			100: '#f5f5f5',
			200: '#eeeeee',
			300: '#e0e0e0',
			400: '#bdbdbd',
			500: '#9e9e9e',
			600: '#757575',
			700: '#616161',
			800: '#424242',
			900: '#212121',
		},
	},
	components: {
		MuiAlert: {
			styleOverrides: {
				/**
				 * MUI tints a standard alert from `palette[severity].light`, which is semi-transparent in
				 * this theme, so the alert came out washed out and its text half transparent. Tint it from
				 * the opaque `main` instead, keeping MUI's 0.6 / 0.9 ratios and its dark mode swap.
				 */
				root: ({ ownerState, theme: { palette } }) => {
					if ((ownerState.variant ?? 'standard') !== 'standard') return {};
					const severity = (ownerState.color ?? ownerState.severity ?? 'success') as AlertColor;
					const main = palette[severity]?.main;
					if (!main) return {};
					const isLight = palette.mode === 'light';
					return {
						color: isLight ? darken(main, 0.6) : lighten(main, 0.6),
						backgroundColor: isLight ? lighten(main, 0.9) : darken(main, 0.9),
						[`& .${alertClasses.icon}`]: { color: main },
					};
				},
			},
		},
	},
});

root.render(
	<Provider store={store}>
		<PersistGate loading={null} persistor={persistor}>
			<ThemeProvider theme={theme}>
				<CssBaseline />
				<App />
				<ToastContainer />
			</ThemeProvider>
		</PersistGate>
	</Provider>,
);
reportWebVitals();
